import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, X, Save, Building2, Tags, Mail, Pencil, User, ShieldCheck, SlidersHorizontal, Users, KeyRound, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import UsersPanel from "@/components/UsersPanel";
import PasskeysPanel from "@/components/PasskeysPanel";

const CORE_ADMIN_EMAILS = ["d.viola@workplaceone.com", "cidd67@gmail.com"];

function RoutingRuleRow({ rule, onChange, onRemove, onAddEmail }) {
  const slug = (s) => s.replace(/\s+/g, "-");
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`routing-rule-${slug(rule.property)}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono bg-slate-900 text-white rounded px-2 py-1">{rule.property}</span>
        <Button variant="ghost" size="sm" onClick={onRemove} className="text-slate-400 hover:text-red-600" data-testid={`routing-remove-rule-${slug(rule.property)}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {rule.emails.map((em) => (
          <span key={em} className="inline-flex items-center gap-1 text-xs font-mono bg-slate-100 border border-slate-200 rounded px-2 py-1" data-testid={`routing-email-${em}`}>
            {em}
            <button type="button" onClick={() => onChange({ emails: rule.emails.filter((x) => x !== em) })} className="text-slate-400 hover:text-red-600" data-testid={`routing-remove-email-${em}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Add email — press Enter"
          value={rule.draft}
          onChange={(e) => onChange({ draft: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); onAddEmail(); } }}
          className="bg-white max-w-xs"
          data-testid={`routing-email-input-${slug(rule.property)}`}
        />
        <Button type="button" variant="outline" size="sm" onClick={onAddEmail} className="bg-white" data-testid={`routing-add-email-${slug(rule.property)}`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function RoutingCard({ api, properties, onSaved }) {
  const [rules, setRules] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [newProp, setNewProp] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${api}/settings/routing`);
        setRules(r.data.routing.map((x) => ({ ...x, draft: "" })));
      } catch {
        toast.error("Failed to load routing rules");
      } finally {
        setLoaded(true);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module-scope constants
  }, [api]);

  const available = properties.filter((p) => !rules.some((r) => r.property.toLowerCase() === p.toLowerCase()));
  const updateRule = (i, patch) => setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const slug = (s) => s.replace(/\s+/g, "-");

  const addRule = () => {
    if (!newProp) { toast.error("Pick a property first"); return; }
    setRules((rs) => [...rs, { property: newProp, emails: [], draft: "" }]);
    setNewProp("");
  };

  const addEmail = (i) => {
    const em = (rules[i].draft || "").trim().toLowerCase().replace(/,$/, "");
    if (!em) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { toast.error("That doesn't look like an email address"); return; }
    if (rules[i].emails.includes(em)) { toast.error("Already added"); return; }
    updateRule(i, { emails: [...rules[i].emails, em], draft: "" });
  };

  const save = async () => {
    if (rules.some((r) => r.emails.length === 0)) { toast.error("Every rule needs at least one email"); return; }
    setSaving(true);
    try {
      await axios.put(`${api}/settings/routing`, rules.map(({ property, emails }) => ({ property, emails })));
      toast.success("Routing rules saved");
      onSaved?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save routing");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm lg:col-span-2" data-testid="routing-card">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-amber-600" />
          <CardTitle className="text-lg">Property Email Routing</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <p className="text-sm text-slate-600">
          Send new work order notifications for specific properties to different email addresses (one or more per property).
          Properties without a rule go to the default notification address above.
        </p>
        {!loaded ? (
          <p className="text-sm text-slate-500">Loading...</p>
        ) : (
          <>
            {rules.length === 0 && <p className="text-sm text-slate-500" data-testid="routing-empty">No routing rules yet.</p>}
            {rules.map((r, i) => (
              <RoutingRuleRow
                key={r.property}
                rule={r}
                onChange={(patch) => updateRule(i, patch)}
                onRemove={() => setRules((rs) => rs.filter((_, j) => j !== i))}
                onAddEmail={() => addEmail(i)}
              />
            ))}
            <div className="flex gap-2">
              <Select value={newProp} onValueChange={setNewProp}>
                <SelectTrigger className="bg-white max-w-xs" data-testid="routing-property-select">
                  <SelectValue placeholder="Select property to route" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {available.map((p) => (<SelectItem key={p} value={p} data-testid={`routing-property-option-${slug(p)}`}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={addRule} className="bg-white" data-testid="routing-add-rule-button">
                <Plus className="w-4 h-4 mr-1.5" />Add Rule
              </Button>
            </div>
            <Button onClick={save} disabled={saving} className="bg-[#478261] hover:bg-[#3a6b51] text-white" data-testid="routing-save-button">
              <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Routing Rules"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function UserRolesCard({ api }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${api}/auth/users`);
        setUsers(r.data);
      } catch {
        toast.error("Failed to load users");
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module-scope constants
  }, [api]);

  const setRole = async (u, role) => {
    setBusyId(u.id);
    try {
      await axios.put(`${api}/auth/users/${u.id}/role`, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role } : x)));
      toast.success(`${u.email} is now ${role}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to update role");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm lg:col-span-2" data-testid="user-roles-card">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-amber-600" />
          <CardTitle className="text-lg">User Roles</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-3">
        <p className="text-sm text-slate-600">
          Admins see the dashboard, stats, reports and settings. Staff can only submit requests. The two core admin accounts are protected.
        </p>
        {loading ? (
          <p className="text-sm text-slate-500">Loading users...</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="user-roles-list">
            {users.map((u) => {
              const role = u.role || "staff";
              const isProtected = CORE_ADMIN_EMAILS.includes(u.email);
              return (
                <li key={u.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`user-role-row-${u.email}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                    <div className="text-xs text-slate-500 font-mono truncate">{u.email}</div>
                  </div>
                  {isProtected ? (
                    <span className="text-xs font-mono text-slate-500 border border-slate-200 rounded-md px-2.5 py-1.5 bg-slate-50 shrink-0" data-testid={`user-role-protected-${u.email}`}>Admin (protected)</span>
                  ) : (
                    <select
                      value={role}
                      disabled={busyId === u.id}
                      onChange={(e) => setRole(u, e.target.value)}
                      className="text-sm border border-slate-300 rounded-md px-2.5 py-1.5 bg-white shrink-0 disabled:opacity-50"
                      data-testid={`user-role-select-${u.email}`}
                    >
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                    </select>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ListEditor({ title, icon: Icon, items, onChange, onSave, saving, testPrefix, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.some((i) => i.toLowerCase() === v.toLowerCase())) { toast.error("Already in the list"); return; }
    onChange([...items, v]);
    setDraft("");
  };
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-amber-600" />
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-list`}>
          {items.map((item) => (
            <span key={item} className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-md px-2.5 py-1 text-sm text-slate-800">
              {item}
              <button
                type="button"
                onClick={() => {
                  const v = window.prompt(`Edit "${item}":`, item);
                  if (v === null) return;
                  const nv = v.trim();
                  if (!nv) return;
                  if (items.some((i) => i !== item && i.toLowerCase() === nv.toLowerCase())) { toast.error("Already in the list"); return; }
                  onChange(items.map((i) => (i === item ? nv : i)));
                }}
                className="text-slate-400 hover:text-amber-600"
                data-testid={`${testPrefix}-edit-${item.replace(/\s+/g, "-")}`}
                aria-label={`Edit ${item}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onChange(items.filter((i) => i !== item))}
                className="text-slate-400 hover:text-red-600"
                data-testid={`${testPrefix}-remove-${item.replace(/\s+/g, "-")}`}
                aria-label={`Remove ${item}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          {items.length === 0 && <span className="text-sm text-slate-400">No items — add at least one.</span>}
        </div>
        <div className="flex gap-2">
          <Input
            className="bg-white"
            placeholder={placeholder}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            data-testid={`${testPrefix}-add-input`}
          />
          <Button type="button" variant="outline" onClick={add} className="bg-white shrink-0" data-testid={`${testPrefix}-add-button`}>
            <Plus className="w-4 h-4 mr-1" />Add
          </Button>
        </div>
        <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">Existing tasks keep their saved value even if you remove it here.</p>
          <Button onClick={onSave} disabled={saving} className="bg-[#478261] hover:bg-[#3a6b51] text-white shrink-0" data-testid={`${testPrefix}-save-button`}>
            <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsPanel({ api, onSaved, currentUser }) {
  const [properties, setProperties] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [requesterNames, setRequesterNames] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${api}/settings`);
        setProperties(r.data.properties);
        setIssueTypes(r.data.issue_types);
        setNotifyEmail(r.data.notify_email || "");
        setRequesterNames(r.data.requester_names || []);
        setLoaded(true);
      } catch (e) {
        toast.error("Failed to load settings");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module-scope constants
  }, [api]);

  const save = async (key, values, setter, pick) => {
    setSaving(true);
    try {
      const r = await axios.put(`${api}/settings/${key}`, { values });
      setter(r.data[pick]);
      toast.success("Saved — dropdowns updated everywhere");
      onSaved?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const saveNotify = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${api}/settings/notify-email`, { email: notifyEmail });
      setNotifyEmail(r.data.notify_email);
      toast.success("Notification email saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Tabs defaultValue="preferences" data-testid="settings-tabs">
      <TabsList className="bg-white border border-slate-200 p-1 h-auto rounded-lg mb-6 flex flex-wrap gap-1">
        <TabsTrigger value="preferences" data-testid="settings-tab-preferences" className="px-4 py-2 data-[state=active]:bg-[#478261] data-[state=active]:text-white"><SlidersHorizontal className="w-4 h-4 mr-2" />Preferences</TabsTrigger>
        <TabsTrigger value="users" data-testid="settings-tab-users" className="px-4 py-2 data-[state=active]:bg-[#478261] data-[state=active]:text-white"><Users className="w-4 h-4 mr-2" />Users</TabsTrigger>
        <TabsTrigger value="passkeys" data-testid="settings-tab-passkeys" className="px-4 py-2 data-[state=active]:bg-[#478261] data-[state=active]:text-white"><KeyRound className="w-4 h-4 mr-2" />Passkeys</TabsTrigger>
      </TabsList>
      <TabsContent value="preferences">
        {!loaded ? (
          <div className="text-sm text-slate-500" data-testid="settings-loading">Loading settings...</div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="settings-panel">
      <ListEditor
        title="Property Choices"
        icon={Building2}
        items={properties}
        onChange={setProperties}
        onSave={() => save("properties", properties, setProperties, "properties")}
        saving={saving}
        testPrefix="settings-properties"
        placeholder="e.g. 123 front street"
      />
      <ListEditor
        title="Issue Type Choices"
        icon={Tags}
        items={issueTypes}
        onChange={setIssueTypes}
        onSave={() => save("issue-types", issueTypes, setIssueTypes, "issue_types")}
        saving={saving}
        testPrefix="settings-issue-types"
        placeholder="e.g. HVAC"
      />
      <ListEditor
        title="Requester Names"
        icon={User}
        items={requesterNames}
        onChange={setRequesterNames}
        onSave={() => save("requester-names", requesterNames, setRequesterNames, "requester_names")}
        saving={saving}
        testPrefix="settings-requester-names"
        placeholder="e.g. Jane Smith"
      />
      <Card className="border-slate-200 shadow-sm lg:col-span-2">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-amber-600" />
            <CardTitle className="text-lg">Submission Notifications</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-5 space-y-3">
          <p className="text-sm text-slate-600">Every new work order submission is emailed to this address instantly.</p>
          <div className="flex gap-2">
            <Input type="email" className="bg-white" placeholder="ops@workplaceone.com" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} data-testid="notify-email-input" />
            <Button onClick={saveNotify} disabled={saving} className="bg-[#478261] hover:bg-[#3a6b51] text-white shrink-0" data-testid="notify-email-save-button">
              <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>
          <RoutingCard api={api} properties={properties} onSaved={onSaved} />
          <UserRolesCard api={api} />
        </div>
        )}
      </TabsContent>
      <TabsContent value="users">
        <UsersPanel api={api} currentUser={currentUser} />
      </TabsContent>
      <TabsContent value="passkeys">
        <PasskeysPanel api={api} />
      </TabsContent>
    </Tabs>
  );
}
